import moment from 'moment/moment';

/**
 * Display helpers for a referral row's `outstandingRequirement` and `qualifyByAt`, shared by
 * DriverReferralDashboard.vue and RiderReferralDashboard.vue.
 *
 * The copy is keyed by referral type because the two programmes do not share conditions: a rider
 * referral has no onboarding or subscription gate, and its cap is terminal rather than a hold. Keeping
 * one map per type in one file is what stops the two dashboards drifting apart — they previously held
 * byte-identical copies of all of this, so a value added to ReferralOutstandingRequirement could be
 * wired into one dashboard and silently render nothing in the other.
 */

const SHARED_COPY = {
  AWAITING_FIRST_PAID_RIDE: 'Waiting on their first paid ride — the 30-day window starts then.',
  MORE_PAID_RIDES: null,
  WINDOW_EXPIRED: 'The 30-day qualification window closed before this referral qualified.',
  UNDER_REVIEW: 'On hold while we review this referral.',
  NOT_ELIGIBLE: 'This referral was not eligible for a reward.',
  COMPLETE: null,
};

const DRIVER_COPY = {
  ...SHARED_COPY,
  REFERRED_ONBOARDING_INCOMPLETE: 'Rides complete. Waiting on them to finish onboarding.',
  REFERRED_SUBSCRIPTION_INACTIVE: 'Rides complete. Waiting on their subscription to become active.',
  REFERRER_CAP_REACHED: "Qualified, but you've reached the maximum rewards for this programme.",
  AWAITING_NEXT_RIDE: 'Everything qualifies — your reward lands on their next paid ride.',
};

const RIDER_COPY = {
  ...SHARED_COPY,
  // No "qualified" here: a rider referral blocked by the cap never met a bar, it earned nothing from
  // the start and cannot recover.
  REFERRER_CAP_REACHED: "You've already earned the maximum referral rewards, so this one is tracked but won't pay out.",
  AWAITING_NEXT_RIDE: 'Everything qualifies — your bonus lands on their next paid ride.',
};

const CLASSES = {
  AWAITING_FIRST_PAID_RIDE: 'text-muted',
  REFERRED_ONBOARDING_INCOMPLETE: 'text-warning-emphasis',
  REFERRED_SUBSCRIPTION_INACTIVE: 'text-warning-emphasis',
  REFERRER_CAP_REACHED: 'text-muted',
  AWAITING_NEXT_RIDE: 'text-success',
  WINDOW_EXPIRED: 'text-muted',
  UNDER_REVIEW: 'text-muted',
  NOT_ELIGIBLE: 'text-muted',
};

const ICONS = {
  AWAITING_FIRST_PAID_RIDE: 'fa-hourglass-start',
  REFERRED_ONBOARDING_INCOMPLETE: 'fa-id-card',
  REFERRED_SUBSCRIPTION_INACTIVE: 'fa-credit-card',
  REFERRER_CAP_REACHED: 'fa-trophy',
  AWAITING_NEXT_RIDE: 'fa-check-circle',
  WINDOW_EXPIRED: 'fa-calendar-times',
  UNDER_REVIEW: 'fa-search',
  NOT_ELIGIBLE: 'fa-ban',
};

/**
 * Explanatory line for a referral row, or null when there is nothing to explain. An unrecognised
 * requirement returns null rather than the raw enum name, so a value the server gains before the
 * frontend does renders as nothing instead of as debug text.
 */
export function outstandingLabel(referral, isDriver) {
  const copy = isDriver ? DRIVER_COPY : RIDER_COPY;
  return copy[referral.outstandingRequirement] || null;
}

export function outstandingClass(referral) {
  return CLASSES[referral.outstandingRequirement] || 'text-muted';
}

export function outstandingIcon(referral) {
  return ICONS[referral.outstandingRequirement] || 'fa-info-circle';
}

/**
 * Whole days remaining until the qualification deadline, or null when there is no deadline to show.
 *
 * Compared at day boundaries rather than with a plain `diff`, because moment's unit diff truncates
 * toward zero: a deadline that passed two hours ago yields 0, which reads as "today is the last day"
 * for the whole 24 hours after the window has actually closed.
 */
export function daysToDeadline(referral) {
  if (!referral.qualifyByAt) return null;
  const deadline = moment(referral.qualifyByAt);
  if (!deadline.isValid()) return null;
  return deadline.startOf('day').diff(moment().startOf('day'), 'days');
}

export function deadlineIsUrgent(referral) {
  const days = daysToDeadline(referral);
  return days !== null && days <= 7;
}

export function deadlineLabel(referral) {
  const days = daysToDeadline(referral);
  if (days === null) return null;
  if (days < 0) return 'Window closed';
  if (days === 0) return 'Last day to qualify';
  return `${days} day${days === 1 ? '' : 's'} left to qualify`;
}
